import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { SearchBar, CategoryGrid } from '@/components/domain';
import { Card, CityPicker, EmptyState, Spinner, readStoredCitySlug } from '@/components/atoms';
import { useAuthStore } from '@/store';
import { catalog } from '@/api';
import { mockParentBuckets } from '@/mocks/categories';
import type { Category } from '@/types/domain';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

export function HomePage(): JSX.Element {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [query, setQuery] = useState('');
  // F-170 B3.4: city selection lives in localStorage (`olabotardivna.city_slug`).
  // The CityPicker is self-controlled; we mirror the value so the search
  // submission can append `?city=` to the URL.
  const [citySlug, setCitySlug] = useState<string | null>(() => readStoredCitySlug());
  const [parents, setParents] = useState<Category[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await catalog.fetchCategories();
        if (cancelled) return;
        const tops = data.results.filter((c) => c.parent_id === null);
        setParents(tops.length ? tops : mockParentBuckets);
      } catch (err) {
        logger.warn('Categories fetch failed, using mocks', err);
        if (!cancelled) {
          setParents(mockParentBuckets);
          setLoadFailed(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isSpecialist = user?.role === 'specialist';

  const onSearch = useMemo(
    () => () => {
      const q = query.trim();
      if (!q) return;
      const params = new URLSearchParams({ q });
      if (citySlug) params.set('city', citySlug);
      navigate(`/search?${params.toString()}`);
    },
    [navigate, query, citySlug],
  );

  return (
    <Layout title="">
      <div className={styles.section}>
        <SearchBar value={query} onChange={setQuery} onSubmit={onSearch} />
        {/* F-170 B3.4: city dropdown above the categories so the filter
            sticks across all entry points. Defaults to whatever the user
            last picked (localStorage). */}
        <CityPicker value={citySlug} onChange={setCitySlug} />
      </div>

      {!user ? (
        <Card>
          <p>Mini App має бути відкрито через Telegram-бота.</p>
        </Card>
      ) : null}

      <section>
        <h2 className={styles.sectionTitle}>Розділи</h2>
        {parents === null ? (
          <Spinner center />
        ) : (
          <CategoryGrid categories={parents} />
        )}
        {loadFailed ? (
          <p className={styles.note}>
            Поки що показано локальний каталог — backend ще не відповів.
          </p>
        ) : null}
      </section>

      {!isSpecialist ? (
        <section>
          <h2 className={styles.sectionTitle}>Швидкий доступ</h2>
          <div className={styles.linksGrid}>
            <Card interactive onClick={() => navigate('/board')}>
              <strong>Шукаю майстра</strong>
              <p className={styles.note}>Опублікуй запит — спеціалісти відгукнуться.</p>
            </Card>
            <Card interactive onClick={() => navigate('/search/saved')}>
              <strong>Збережені пошуки</strong>
              <p className={styles.note}>Алерти про нових спеціалістів.</p>
            </Card>
            <Card interactive onClick={() => navigate('/favorites')}>
              <strong>Закладки</strong>
              <p className={styles.note}>Збережені профілі.</p>
            </Card>
          </div>
        </section>
      ) : (
        <section>
          <h2 className={styles.sectionTitle}>Для спеціаліста</h2>
          <div className={styles.linksGrid}>
            <Card interactive onClick={() => navigate('/me/crm')}>
              <strong>CRM</strong>
              <p className={styles.note}>Заявки, активні, завершені.</p>
            </Card>
            <Card interactive onClick={() => navigate('/me/calendar')}>
              <strong>Календар</strong>
              <p className={styles.note}>Робочі години + блоки.</p>
            </Card>
            <Card interactive onClick={() => navigate('/me/stats')}>
              <strong>Статистика</strong>
              <p className={styles.note}>Перегляди, конверсія.</p>
            </Card>
          </div>
        </section>
      )}

      {parents === null && loadFailed ? (
        <EmptyState
          icon="sad"
          title="Каталог зараз тихий"
          message="Backend поки не відповідає. Спробуй пізніше."
        />
      ) : null}
    </Layout>
  );
}
